import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet';
import {
  GraduationCap,
  CheckCircle2,
  Clock,
  Building2,
  Briefcase,
  Stethoscope,
  ArrowRight,
  ChevronRight,
  AlertTriangle,
  FileText,
  Target,
  MapPin,
  Award,
  BookOpen,
  Calendar,
} from 'lucide-react';

const PLABGuidePage = () => {
  const publishDate = '2025-01-15';
  const modifiedDate = new Date().toISOString().split('T')[0];

  const afterPLAB = [
    {
      icon: FileText,
      title: 'Apply for GMC Registration',
      description: 'Once you pass PLAB 2, apply for GMC registration with a licence to practise. This typically takes 2-4 weeks.',
    },
    {
      icon: Target,
      title: 'Identify Your Target Specialty',
      description: 'Decide which specialty you want to work in. This helps you focus your job search on relevant LED and Trust Grade positions.',
    },
    {
      icon: Building2,
      title: 'Apply for LED/Trust Grade Jobs',
      description: 'These non-training posts are the fastest route to NHS employment. They\'re fully open to PLAB-qualified doctors.',
    },
    {
      icon: Clock,
      title: 'Build NHS Experience',
      description: 'Start accumulating UK clinical experience. This strengthens future training applications under the 2026 Act.',
    },
  ];

  const jobTypes = [
    {
      title: 'Trust Grade / LED',
      level: 'SHO to Registrar equivalent',
      description: 'The most common entry point for PLAB doctors. Direct employment by NHS Trusts, typically in general medicine, surgery, or emergency medicine.',
      ideal: true,
      link: '/img-guide/trust-grade-jobs',
    },
    {
      title: 'Clinical Fellow',
      level: 'Registrar equivalent',
      description: 'Often include teaching or research components. Popular in academic hospitals and teaching trusts.',
      ideal: true,
    },
    {
      title: 'Staff Grade',
      level: 'Middle grade',
      description: 'More senior than Trust Grade, often requiring additional experience. Good for doctors with overseas specialty experience.',
      ideal: false,
    },
    {
      title: 'Specialty Doctor',
      level: 'Career grade',
      description: 'Senior non-training role, typically requires 4+ years post-qualification experience. Long-term career option.',
      ideal: false,
    },
  ];

  const tips = [
    {
      title: 'Start Searching Before PLAB 2',
      description: 'Many Trusts accept applications from doctors awaiting GMC registration. Begin your job search while preparing for PLAB 2.',
    },
    {
      title: 'Cast a Wide Geographic Net',
      description: 'Your first NHS job doesn\'t have to be in London. Less competitive areas often have more vacancies and faster hiring.',
    },
    {
      title: 'Consider Less Competitive Specialties',
      description: 'General Medicine, Psychiatry, and Emergency Medicine often have the most LED vacancies for new doctors.',
    },
    {
      title: 'Set Up Multiple Alerts',
      description: 'Create alerts for different specialties and locations to maximise your options. LED jobs can fill within 48 hours.',
    },
    {
      title: 'Prepare a Strong NHS-Style CV',
      description: 'UK medical CVs have a specific format. Include your GMC number, right to work status, and UK referees if possible.',
    },
    {
      title: 'Practice for NHS Interviews',
      description: 'NHS interviews often include scenario-based questions about clinical governance, patient safety, and teamwork.',
    },
  ];

  const faqs = [
    {
      question: 'How soon after PLAB 2 can I start working in the NHS?',
      answer: 'Once you receive your GMC registration (typically 2-4 weeks after passing PLAB 2), you can start work immediately if you have a job offer. Some doctors secure offers before receiving GMC registration, with start dates contingent on registration approval.',
    },
    {
      question: 'What jobs can I get with just PLAB (no UK experience)?',
      answer: 'PLAB-qualified doctors can apply for Trust Grade, LED (Locally Employed Doctor), Clinical Fellow, and Staff Grade positions. These non-training posts are fully open to doctors without prior UK experience. Most PLAB doctors start in Trust Grade or LED roles.',
    },
    {
      question: 'Do I need to do an NHS internship or FY1 after PLAB?',
      answer: 'No. If you have completed a recognised internship in your home country, you do not need to repeat Foundation Year 1 in the UK. Your GMC registration will reflect this. You can apply directly for SHO-equivalent Trust Grade or LED positions.',
    },
    {
      question: 'How does the 2026 Prioritisation Act affect PLAB doctors?',
      answer: 'The Medical Training Prioritisation Act 2026 affects formal training posts (Foundation Programme, Specialty Training) but NOT non-training posts. LED, Trust Grade, Clinical Fellow, and Staff Grade jobs remain fully open to PLAB-qualified IMGs on equal terms.',
      link: '/img-guide/medical-training-prioritisation-act-2026',
      linkText: 'Read our full guide on the Act →',
    },
    {
      question: 'Can I get visa sponsorship with a PLAB qualification?',
      answer: 'Yes. Many NHS Trusts sponsor Skilled Worker visas for doctors in LED and Trust Grade positions. Your PLAB qualification and GMC registration demonstrate you meet UK medical standards, making visa sponsorship more straightforward.',
    },
    {
      question: 'How competitive are NHS jobs for PLAB doctors?',
      answer: 'Competition varies by specialty and location. Popular specialties in London are very competitive. However, there are consistently vacancies in General Medicine, Psychiatry, Emergency Medicine, and in hospitals outside major cities. Being flexible increases your chances significantly.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>PLAB Doctor Jobs UK | NHS Alerts for IMGs — TRACJobsAlert</title>
        <meta name="description" content="Find NHS jobs after passing PLAB. Get instant alerts for Trust Grade, LED, and Clinical Fellow positions open to PLAB-qualified international medical graduates. Start your UK medical career." />
        <meta name="keywords" content="PLAB doctor jobs UK, jobs after PLAB, NHS jobs for PLAB doctors, PLAB qualified jobs UK, GMC registration jobs, IMG jobs after PLAB, PLAB 2 jobs UK, NHS career after PLAB" />
        <link rel="canonical" href="https://tracjobsalert.co.uk/img-guide/plab-jobs" />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://tracjobsalert.co.uk/img-guide/plab-jobs" />
        <meta property="og:title" content="PLAB Doctor Jobs UK | NHS Alerts for IMGs" />
        <meta property="og:description" content="Find NHS jobs after passing PLAB. Get instant alerts for Trust Grade, LED, and Clinical Fellow positions." />
        <meta property="og:image" content="https://tracjobsalert.co.uk/icon-512.png" />
        <meta property="og:site_name" content="TRACJobsAlert" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="PLAB Doctor Jobs UK | TRACJobsAlert" />
        <meta name="twitter:description" content="Complete guide to finding NHS jobs after passing PLAB exams." />
        
        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "PLAB Doctor Jobs UK: Finding NHS Employment After PLAB",
            "description": "Complete guide for PLAB-qualified doctors seeking NHS employment. Learn what jobs you can apply for, how to get started, and tips for securing your first UK position.",
            "author": {
              "@type": "Organization",
              "name": "TRACJobsAlert"
            },
            "publisher": {
              "@type": "Organization",
              "name": "TRACJobsAlert",
              "logo": {
                "@type": "ImageObject",
                "url": "https://tracjobsalert.co.uk/icon-512.png"
              }
            },
            "datePublished": publishDate,
            "dateModified": modifiedDate,
            "mainEntityOfPage": "https://tracjobsalert.co.uk/img-guide/plab-jobs"
          })}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <Stethoscope className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="font-bold text-slate-900">Trac Jobs Alert</span>
                  <span className="text-xs text-emerald-600 block -mt-1">For Doctors</span>
                </div>
              </Link>
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-600">Sign In</Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full">
                    Get Alerts
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Breadcrumb */}
        <div className="bg-slate-50 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <nav className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-slate-500 hover:text-emerald-600">Home</Link>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <Link to="/img-guide" className="text-slate-500 hover:text-emerald-600">IMG Guide</Link>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className="text-slate-900 font-medium">PLAB Jobs</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-b from-slate-50 to-white py-12 lg:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-sm font-medium mb-6">
              <GraduationCap className="w-4 h-4" />
              Post-PLAB Career Guide
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              PLAB Doctor Jobs UK
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
              Passed PLAB? Your NHS career starts here. Find <Link to="/img-guide/trust-grade-jobs" className="text-emerald-600 hover:text-emerald-700 underline">Trust Grade</Link>, <Link to="/img-guide/led-jobs" className="text-emerald-600 hover:text-emerald-700 underline">LED</Link>, and Clinical Fellow positions 
              open to PLAB-qualified international medical graduates. Get instant alerts for new vacancies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 text-lg">
                  Get NHS Job Alerts
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* After PLAB Section */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 text-center">
              Your Path After Passing PLAB
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto text-center mb-12">
              Follow these steps to transition from PLAB success to NHS employment.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {afterPLAB.map((item, index) => (
                <div key={index} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Job Types */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 text-center">
              Jobs You Can Apply For
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto text-center mb-12">
              PLAB qualifies you for various NHS positions. Here are your options.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {jobTypes.map((job, index) => (
                <div 
                  key={index} 
                  className={`rounded-xl p-6 border ${job.ideal ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-lg text-slate-900">{job.title}</h3>
                    {job.ideal && (
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                        Best for new PLAB doctors
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-purple-600 font-medium mb-2">{job.level}</p>
                  <p className="text-slate-600 text-sm">{job.description}</p>
                  {job.link && (
                    <Link to={job.link} className="text-emerald-600 text-sm font-medium mt-3 inline-flex items-center gap-1 hover:text-emerald-700">
                      Learn more about {job.title} →
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 text-center">
              Tips for PLAB Doctors Job Hunting
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto text-center mb-12">
              Practical advice to help you secure your first NHS position faster.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tips.map((tip, index) => (
                <div key={index} className="bg-white rounded-xl p-6 border border-slate-200">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 mt-0.5 flex-shrink-0 text-emerald-500" />
                    <div>
                      <h3 className="font-semibold text-lg text-slate-900 mb-2">{tip.title}</h3>
                      <p className="text-slate-600 text-sm">{tip.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-purple-600">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Start Your NHS Career Today
            </h2>
            <p className="text-lg text-purple-100 mb-8 max-w-2xl mx-auto">
              PLAB jobs get snapped up quickly. Get instant alerts when new Trust Grade, LED, and Clinical Fellow positions are posted.
            </p>
            <Link to="/register">
              <Button className="bg-white text-purple-600 hover:bg-purple-50 rounded-full px-8 py-6 text-lg font-semibold">
                Get Job Alerts — £9.99/month
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-purple-200 text-sm mt-4">Cancel anytime. No contract.</p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 text-center">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto text-center mb-12">
              Common questions from PLAB-qualified doctors about NHS employment.
            </p>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{faq.question}</h3>
                  <p className="text-slate-600">{faq.answer}</p>
                  {faq.link && (
                    <Link to={faq.link} className="text-emerald-600 text-sm font-medium mt-2 inline-flex items-center gap-1 hover:text-emerald-700">
                      {faq.linkText}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Related Guides</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/img-guide/trust-grade-jobs" className="bg-white rounded-xl p-6 border border-slate-200 hover:border-emerald-500 transition-colors">
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Trust Grade Jobs</h3>
                <p className="text-slate-600 text-sm mb-4">Complete guide to Trust Grade doctor positions.</p>
                <span className="text-emerald-600 text-sm font-medium flex items-center gap-1">
                  Read guide <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
              <Link to="/img-guide/led-jobs" className="bg-white rounded-xl p-6 border border-slate-200 hover:border-emerald-500 transition-colors">
                <h3 className="font-semibold text-lg text-slate-900 mb-2">LED Doctor Jobs</h3>
                <p className="text-slate-600 text-sm mb-4">Everything about Locally Employed Doctor roles.</p>
                <span className="text-emerald-600 text-sm font-medium flex items-center gap-1">
                  Read guide <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
              <Link to="/img-guide/medical-training-prioritisation-act-2026" className="bg-white rounded-xl p-6 border border-slate-200 hover:border-emerald-500 transition-colors">
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Medical Training Prioritisation Act</h3>
                <p className="text-slate-600 text-sm mb-4">What the 2026 Act means for your career.</p>
                <span className="text-emerald-600 text-sm font-medium flex items-center gap-1">
                  Read guide <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <Stethoscope className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="font-bold text-white">Trac Jobs Alert</span>
                  <span className="text-xs text-emerald-400 block">For Doctors</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-6 text-sm">
                <Link to="/contact" className="text-slate-400 hover:text-white">Contact</Link>
                <Link to="/privacy" className="text-slate-400 hover:text-white">Privacy Policy</Link>
                <Link to="/terms" className="text-slate-400 hover:text-white">Terms of Service</Link>
                <Link to="/img-guide" className="text-slate-400 hover:text-white">IMG Guide</Link>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
              © 2025 TRACJobsAlert. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PLABGuidePage;
